import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { TenantBranding, TenantContext, DEFAULT_BRANDING } from '../types/branding';
import { brandingService, isFlowPage, PLATFORM_TENANT } from '../services/brandingService';

/**
 * The branding for the current page, applied to the document. Pages of an
 * application's sign-in (login, registration, verification, 2FA, consent)
 * wear the branding of the tenant that owns the application; QAIM's own
 * pages (home, settings, help) wear the platform's.
 *
 * `loading` is true only until the first branding arrives. Later changes
 * (moving between pages) swap in place, so a page is never unmounted while
 * branding reloads.
 */
export function useBranding(tenantId?: string) {
  const location = useLocation();
  const flow = !tenantId && isFlowPage(location.pathname);
  const [branding, setBranding] = useState<TenantBranding>(DEFAULT_BRANDING);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    let active = true;

    const loadBranding = async () => {
      try {
        setError(null);
        const brandingData = tenantId
          ? await brandingService.loadBranding(tenantId)
          : flow
            ? await brandingService.loadFlowBranding(location.search)
            : await brandingService.loadBranding(PLATFORM_TENANT);
        if (!active) return;
        setBranding(brandingData);
        brandingService.applyBranding(brandingData);
      } catch (err) {
        if (!active) return;
        setError(err as Error);
        console.error('Failed to load branding:', err);
      } finally {
        if (active) setLoading(false);
      }
    };

    void loadBranding();
    return () => {
      active = false;
    };
  }, [tenantId, flow, location.search]);

  return { branding, loading, error };
}

/**
 * Hook to load full tenant context (name, enabled components). Branding is
 * applied by the layout (useBranding), not here, so a page asking for its
 * context can't paint over the sign-in's tenant branding.
 */
export function useTenantContext(tenantId?: string) {
  const [context, setContext] = useState<TenantContext | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    const loadContext = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const tid = tenantId || brandingService.detectTenantId();
        const contextData = await brandingService.loadTenantContext(tid);
        
        setContext(contextData);
      } catch (err) {
        setError(err as Error);
        console.error('Failed to load tenant context:', err);
      } finally {
        setLoading(false);
      }
    };

    loadContext();
  }, [tenantId]);

  return { context, loading, error };
}
